import React, { useState, useEffect } from 'react';
import { Character, Element, Pillar } from '../types';
import { charactersAPI, elementsAPI, pillarsAPI } from '../services/api';
import './SidePanel.css';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  entity: Character | Element | Pillar;
  field: string;
  title: string;
  onUpdate: (updatedEntity: Character | Element | Pillar) => void;
}

interface SidePanelElement {
  _id: string;
  name: string;
  slug: string;
  createdAt?: Date;
  // Additional fields for different entity types
  summary?: string;
  description?: string;
  article?: string;
  category?: string;
  rarity?: string;
  level?: number;
  L1pillar?: string;
  levelFromL1?: number;
}

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  entity,
  field,
  title,
  onUpdate
}) => {
  const [elements, setElements] = useState<(Element | Pillar)[]>([]);
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [displayedElements, setDisplayedElements] = useState<string[]>([]);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newElementName, setNewElementName] = useState('');
  const [addingElement, setAddingElement] = useState(false);
  const [sortType, setSortType] = useState<'name-asc' | 'name-desc' | 'newest' | 'oldest'>('name-asc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showQuickAddPillar, setShowQuickAddPillar] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    name: '',
    pillarParent: ''
  });
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddError, setQuickAddError] = useState('');
  const [allPillars, setAllPillars] = useState<Pillar[]>([]);
  const [saveProgress, setSaveProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Fields that should only allow one displayed element (radio button style)
  const singleSelectionFields = ['species', 'gender', 'beliefs', 'family', 'affiliations', 'pillar'];
  const isSingleSelection = singleSelectionFields.includes(field);

  // Get current field values
  const getCurrentFieldValues = () => {
    const fieldData = (entity as any)[field];
    
    // Special handling for pillar field - it's now an array of objects
    if (field === 'pillar') {
      if (Array.isArray(fieldData)) {
        return fieldData.map((item: any) => {
          if (typeof item === 'object' && item._id) {
            return item._id;
          } else if (typeof item === 'string') {
            return item;
          }
          return item;
        }).filter(Boolean);
      }
      return [];
    }
    
    if (Array.isArray(fieldData)) {
      return fieldData.map((item: any) => {
        // Handle both object with _id and direct string/ID values
        if (typeof item === 'object' && item._id) {
          return item._id;
        } else if (typeof item === 'string') {
          return item;
        }
        return item;
      }).filter(Boolean); // Remove any undefined/null values
    }
    return [];
  };

  // Get currently displayed elements for this field
  const getCurrentDisplayedValues = () => {
    const fieldData = (entity as any)[field];
    
    // Special handling for pillar field - it's now an array of objects
    if (field === 'pillar') {
      if (Array.isArray(fieldData)) {
        return fieldData.map((item: any) => {
          if (typeof item === 'object' && item._id) {
            return item._id;
          } else if (typeof item === 'string') {
            return item;
          }
          return item;
        }).filter(Boolean);
      }
      return [];
    }
    
    if (Array.isArray(fieldData) && fieldData.length > 0) {
      // For single selection fields, the first element is the displayed one
      const singleSelectionFields = ['species', 'gender', 'beliefs', 'family', 'affiliations', 'pillar'];
      const isSingleSelection = singleSelectionFields.includes(field);
      
      if (isSingleSelection) {
        // Return the first element as the displayed element
        const firstElement = fieldData[0];
        if (typeof firstElement === 'object' && firstElement._id) {
          return [firstElement._id];
        } else if (typeof firstElement === 'string') {
          return [firstElement];
        }
        return [];
      } else {
        // For multiple selection, return all elements (they're all displayed)
        return fieldData.map((item: any) => {
          if (typeof item === 'object' && item._id) {
            return item._id;
          } else if (typeof item === 'string') {
            return item;
          }
          return item;
        }).filter(Boolean);
      }
    }
    return [];
  };

  // Load elements on mount
  useEffect(() => {
    console.log('🔄 SidePanel useEffect triggered:', { isOpen, field, title });
    if (isOpen) {
      console.log('🔄 SidePanel useEffect triggered:', { isOpen, field, title });
      console.log('🔄 Current entity data:', entity);
      loadElements();
      // Pre-select the elements that are already assigned to this field
      const currentValues = getCurrentFieldValues();
      const currentDisplayedValues = getCurrentDisplayedValues();
      console.log('🔄 Pre-selecting elements for field:', field, 'Values:', currentValues);
      console.log('🔄 Pre-selecting displayed elements for field:', field, 'Values:', currentDisplayedValues);
      setSelectedElements(currentValues);
      setDisplayedElements(currentDisplayedValues);
    }
  }, [isOpen, field, entity._id]);

  // Auto-sync displayed elements with selected elements for multiple selection
  useEffect(() => {
    if (!isSingleSelection) {
      setDisplayedElements(selectedElements);
    }
  }, [selectedElements, isSingleSelection]);

  // Load all pillars for parent selection in quick add
  useEffect(() => {
    if (isOpen && field === 'pillar') {
      pillarsAPI.getAll().then(setAllPillars).catch(() => setAllPillars([]));
    }
  }, [isOpen, field]);

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sort-container')) {
        setShowSortMenu(false);
      }
    };

    if (showSortMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortMenu]);

  useEffect(() => {
    if (isOpen) {
      setShowQuickAddPillar(false);
    }
  }, [isOpen]);

  const loadElements = async () => {
    try {
      console.log('🔄 loadElements called with field:', field);
      // If the field is 'pillar', load pillars instead of elements
      if (field === 'pillar') {
        console.log('🔄 Loading pillars for pillar field...');
        const pillarsData = await pillarsAPI.getAll();
        console.log('🔄 Pillars loaded:', pillarsData);
        setElements(pillarsData);
      } else {
        console.log('🔄 Loading elements for field:', field);
        const elementsData = await elementsAPI.getAll();
        console.log('🔄 Elements loaded:', elementsData);
        setElements(elementsData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleElementToggle = (elementId: string) => {
    setSelectedElements(prev => {
      if (prev.includes(elementId)) {
        return prev.filter(id => id !== elementId);
      } else {
        return [...prev, elementId];
      }
    });
  };

  const handleDisplayedToggle = (elementId: string) => {
    setDisplayedElements(prev => {
      if (isSingleSelection) {
        // Only update if a different chip is clicked
        if (prev[0] === elementId) {
          return prev; // No change if already selected
        } else {
          return [elementId];
        }
      } else {
        // Multiple selection behavior - not used anymore since elements auto-display
        return prev;
      }
    });
  };

  // Drag and drop functions for multiple selection
  const handleDragStart = (e: React.DragEvent, elementId: string) => {
    if (!isSingleSelection) {
      console.log('🔄 Drag start:', elementId);
      setDraggedElement(elementId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', elementId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isSingleSelection) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (e: React.DragEvent, targetElementId: string) => {
    if (isSingleSelection || !draggedElement || draggedElement === targetElementId) return;

    e.preventDefault();
    console.log('🔄 Drop:', { draggedElement, targetElementId });
    
    setDisplayedElements(prev => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedElement);
      const targetIndex = newOrder.indexOf(targetElementId);
      
      console.log('🔄 Reordering:', { draggedIndex, targetIndex, newOrder });
      
      // Remove dragged element from its current position
      newOrder.splice(draggedIndex, 1);
      
      // Insert dragged element at target position
      newOrder.splice(targetIndex, 0, draggedElement);
      
      return newOrder;
    });
    
    setDraggedElement(null);
  };

  const handleDragEnd = () => {
    if (!isSingleSelection) {
      console.log('🔄 Drag end');
      setDraggedElement(null);
    }
  };

  // Sort displayed elements with a specific sort type
  const handleSortWithType = (sortTypeToUse: 'name-asc' | 'name-desc' | 'newest' | 'oldest') => {
    console.log('🔄 Sorting with type:', sortTypeToUse);
    console.log('🔄 Elements to sort:', displayedElements);
    console.log('🔄 Available elements:', elements);
    
    setDisplayedElements(prev => {
      const sortedElements = [...prev].sort((a, b) => {
        const elementA = elements.find(e => e._id === a);
        const elementB = elements.find(e => e._id === b);
        
        console.log('🔄 Comparing:', { 
          elementA: elementA?.name, 
          elementB: elementB?.name,
          elementACreatedAt: elementA?.createdAt,
          elementBCreatedAt: elementB?.createdAt
        });
        
        if (!elementA || !elementB) return 0;
        
        switch (sortTypeToUse) {
          case 'name-asc':
            return elementA.name.localeCompare(elementB.name);
          case 'name-desc':
            return elementB.name.localeCompare(elementA.name);
          case 'newest':
            // Handle different date formats and fallback to name if no date
            const dateA = elementA.createdAt ? new Date(elementA.createdAt).getTime() : 0;
            const dateB = elementB.createdAt ? new Date(elementB.createdAt).getTime() : 0;
            if (dateA === 0 && dateB === 0) {
              // If neither has a date, fallback to name sorting
              return elementA.name.localeCompare(elementB.name);
            }
            return dateB - dateA;
          case 'oldest':
            // Handle different date formats and fallback to name if no date
            const dateAOldest = elementA.createdAt ? new Date(elementA.createdAt).getTime() : 0;
            const dateBOldest = elementB.createdAt ? new Date(elementB.createdAt).getTime() : 0;
            if (dateAOldest === 0 && dateBOldest === 0) {
              // If neither has a date, fallback to name sorting
              return elementA.name.localeCompare(elementB.name);
            }
            return dateAOldest - dateBOldest;
          default:
            return elementA.name.localeCompare(elementB.name);
        }
      });
      
      console.log('🔄 Sorted result:', sortedElements);
      return sortedElements;
    });
  };

  // Sort displayed elements based on selected sort type (for backward compatibility)
  const handleSortDisplayed = () => {
    handleSortWithType(sortType);
  };

  // For single selection fields, we'll reorder the main array to put the selected element first
  // For multiple selection fields, we'll save the elements in the display order
  const prepareUpdateData = () => {
    // Special handling for pillar field - it should be an array of IDs for multiple selection
    if (field === 'pillar') {
      // Always put the displayed pillar first, then the rest
      if (displayedElements.length > 0) {
        const displayed = displayedElements[0];
        const reordered = [displayed, ...selectedElements.filter(id => id !== displayed)];
        return { [field]: reordered };
      }
      return { [field]: selectedElements };
    }
    
    if (isSingleSelection && displayedElements.length > 0) {
      // Reorder the selected elements array to put the displayed element first
      const displayedElement = displayedElements[0];
      const reorderedElements = [displayedElement, ...selectedElements.filter(id => id !== displayedElement)];
      console.log('🔄 prepareUpdateData (single selection with displayed):', {
        field: field,
        displayedElement: displayedElement,
        selectedElements: selectedElements,
        reorderedElements: reorderedElements
      });
      return { [field]: reorderedElements };
    } else if (isSingleSelection) {
      // For single selection with no displayed element, just send selected elements
      console.log('🔄 prepareUpdateData (single selection no displayed):', {
        field: field,
        selectedElements: selectedElements
      });
      return { [field]: selectedElements };
    } else {
      // For multiple selection, save the elements in the display order
      console.log('🔄 prepareUpdateData (multiple selection):', {
        field: field,
        displayedElements: displayedElements
      });
      return { [field]: displayedElements };
    }
  };

  const handleSave = async () => {
    const entityId = (entity as any)._id;
    if (!entityId) return;

    try {
      setIsSaving(true);
      setSaveProgress(0);
      let updatedEntity;

      // Prepare update data based on field type
      const updateData = prepareUpdateData();
      
      console.log('🔄 Saving with data:', updateData);
      console.log('🔄 Entity being updated:', {
        id: entityId,
        name: (entity as any).name,
        type: 'species' in entity ? 'Character' : 'category' in entity ? 'Element' : 'L1pillar' in entity ? 'Pillar' : 'Unknown',
        field: field,
        title: title
      });
      
      setSaveProgress(25);
      
      // Determine which API to use based on entity type
      // The entity being updated is the one that contains the field, not the field type itself
      if ('species' in entity) {
        // Character (has species, weapons, armor, items, abilities, difficulty, etc.)
        console.log('🔄 Using charactersAPI.update');
        updatedEntity = await charactersAPI.update(entityId, updateData as any);
      } else if ('category' in entity) {
        // Element (has category, rarity, properties, usage, links, pillar, etc.)
        console.log('🔄 Using elementsAPI.update');
        updatedEntity = await elementsAPI.update(entityId, updateData as any);
      } else if ('L1pillar' in entity) {
        // Pillar (has L1pillar, levelFromL1, level, pillarParent, elements, characters, etc.)
        console.log('🔄 Using pillarsAPI.update');
        updatedEntity = await pillarsAPI.update(entityId, updateData as any);
      } else {
        // Fallback to element if unsure
        console.log('🔄 Using elementsAPI.update (fallback)');
        updatedEntity = await elementsAPI.update(entityId, updateData as any);
      }

      setSaveProgress(75);
      console.log('🔄 Updated entity received:', updatedEntity);
      
      // Extract the actual entity from the response if it's wrapped in a 'doc' property
      const finalEntity = (updatedEntity as any).doc || updatedEntity;
      console.log('🔄 Final entity to update:', finalEntity);

      console.log('🔄 Calling onUpdate callback with:', finalEntity);
      onUpdate(finalEntity);
      
      setSaveProgress(90);
      
      // Reload the data to reflect the changes
      await loadElements();
      
      // Re-select the current values after the update
      const getFieldValuesFromEntity = (entityObj: any) => {
        const fieldData = entityObj[field];
        if (field === 'pillar') {
          if (Array.isArray(fieldData)) {
            return fieldData.map((item: any) => (item && item._id) ? item._id : item).filter(Boolean);
          }
          return [];
        }
        if (Array.isArray(fieldData)) {
          return fieldData.map((item: any) => (item && item._id) ? item._id : item).filter(Boolean);
        }
        return [];
      };
      const updatedCurrentValues = getFieldValuesFromEntity(finalEntity);
      setSelectedElements(updatedCurrentValues);
      setDisplayedElements(updatedCurrentValues.length ? [updatedCurrentValues[0]] : []);
      
      setSaveProgress(100);
      
      console.log('🔄 Calling onClose callback');
      onClose();
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast(`${title} updated successfully!`, 'success');
      }
    } catch (error) {
      console.error('Failed to update:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update. Please try again.', 'error');
      }
    } finally {
      setIsSaving(false);
      setSaveProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedElements(getCurrentFieldValues());
    onClose();
  };

  const handleAddNewElement = async () => {
    if (!newElementName.trim()) return;

    try {
      setAddingElement(true);
      const newElement = await elementsAPI.create({
        name: newElementName.trim(),
        description: `Quickly added element: ${newElementName.trim()}`,
        summary: `Quick element: ${newElementName.trim()}`
      });

      // Reload the elements list to get the updated data from server
      await loadElements();
      
      // Find the newly created element and select it
      const createdElement = elements.find(e => e.name === newElementName.trim());
      if (createdElement) {
        setSelectedElements(prev => [...prev, createdElement._id]);
      }
      
      setNewElementName('');
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast(`Element "${newElementName.trim()}" created and added!`, 'success');
      }
    } catch (error) {
      console.error('Failed to create element:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to create element. Please try again.', 'error');
      }
    } finally {
      setAddingElement(false);
    }
  };

  const handleQuickAddPillar = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!quickAddForm.name.trim()) {
      setQuickAddError('Name is required');
      return;
    }

    try {
      setQuickAddLoading(true);
      setQuickAddError('');

      const apiResponse = await pillarsAPI.create({
        name: quickAddForm.name.trim(),
        ...(quickAddForm.pillarParent ? { pillarParent: String(quickAddForm.pillarParent) } : {})
      });
      
      // Log the API response
      console.log('API create pillar response:', apiResponse);
      
      // Use the correct object for the new pillar
      let newPillar: Pillar;
      if (apiResponse && typeof apiResponse === 'object' && 'pillar' in apiResponse) {
        newPillar = (apiResponse as any).pillar;
      } else {
        newPillar = apiResponse as Pillar;
      }
      
      // Map fields if needed
      if ('id' in newPillar && !('_id' in newPillar)) {
        (newPillar as any)._id = (newPillar as any).id;
      }
      if ('Name' in newPillar && !('name' in newPillar)) {
        (newPillar as any).name = (newPillar as any).Name;
      }
      
      // Refresh all pillars to ensure hierarchy and relationships are properly updated
      const refreshAllPillars = async () => {
        try {
          console.log('🔄 SidePanel: Refreshing all pillars after quick creation...');
          const refreshedPillars = await pillarsAPI.getAll();
          setAllPillars(refreshedPillars);
          console.log('🔄 SidePanel: All pillars refreshed successfully after quick creation');
        } catch (error) {
          console.error('🔄 SidePanel: Failed to refresh all pillars after quick creation:', error);
          // Fallback to adding just the new pillar
          setAllPillars(prev => [...prev, newPillar]);
        }
      };
      
      await refreshAllPillars();
      
      // Add the new pillar to the elements list and select it
      setElements(prev => [...prev, newPillar]);
      setSelectedElements(prev => [...prev, newPillar._id]);
      setSearchQuery(''); // Clear search so new pillar is visible
      
      // Reset form and close quick add
      setQuickAddForm({
        name: '',
        pillarParent: ''
      });
      setShowQuickAddPillar(false);
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast(`Pillar "${newPillar.name}" created and selected!`, 'success');
      }
    } catch (error) {
      console.error('Failed to create new pillar:', error);
      setQuickAddError('Failed to create new pillar. Please try again.');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleQuickAddInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setQuickAddForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Always derive filteredElements from elements and searchQuery, excluding the current entity
  const filteredElements = elements.filter(element =>
    element.name && 
    element.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    element._id !== entity._id
  );

  // Debug logging for all fields
  console.log('🔄 SidePanel debug:', {
    field,
    title,
    totalElements: elements.length,
    filteredElements: filteredElements.length,
    searchQuery,
    elements: elements.map(e => ({ id: e._id, name: e.name }))
  });

  // console.log('🔄 SidePanel render:', { isOpen, field, title });
  if (!isOpen) return null;
  
  // console.log('🔄 SidePanel is rendering the overlay');

  return (
    <div className="modal-side-panel-overlay" onClick={onClose}>
      <div className="modal-side-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-side-panel__header">
          <h3>Update {title}</h3>
          <button className="close-side-panel" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-side-panel__content">
          <div className="modal-side-panel__search">
            <input
              type="text"
              placeholder={`Search ${field === 'pillar' ? 'pillars' : 'elements'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="modal-side-panel__search-input"
            />
          </div>

          <div
            className="modal-side-panel__elements"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.5rem',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            {filteredElements.map(item => (
              <label key={item._id} className="modal-side-panel__element-item">
                <input
                  type="checkbox"
                  checked={selectedElements.includes(item._id)}
                  onChange={() => handleElementToggle(item._id)}
                />
                <span>{item.name}</span>
              </label>
            ))}
          </div>

          {/* Summary chips for selected elements */}
          <h4>Selected ({selectedElements.length}):</h4>
          <div className="modal-side-panel__summary-chips">
            {selectedElements.map(itemId => {
              const item = elements.find(e => e._id === itemId);
              const isDisplayed = displayedElements[0] === itemId;
              return item ? (
                <span
                  key={itemId}
                  className={`summary-chip${isDisplayed ? ' displayed' : ' grayed'}`}
                  title={isDisplayed ? 'Displayed on card' : 'Not displayed'}
                >
                  {item.name}
                  {/* Display toggle for single-selection fields */}
                  {isSingleSelection && (
                    <button
                      className={`display-toggle-btn${isDisplayed ? ' displayed' : ''}`}
                      onClick={e => {
                        e.stopPropagation();
                        handleDisplayedToggle(itemId);
                      }}
                      title={isDisplayed ? 'Displayed on card' : 'Set as displayed on card'}
                      style={{ marginLeft: 8 }}
                    >
                      {isDisplayed ? '🔘' : '⚪'}
                    </button>
                  )}
                  {/* Remove button for all chips */}
                  <button
                    className="chip-remove-btn"
                    onClick={() => handleElementToggle(itemId)}
                    title="Remove from selection"
                    style={{ marginLeft: 4 }}
                  >
                    ×
                  </button>
                </span>
              ) : null;
            })}
          </div>
          {field === 'pillar' && selectedElements.length > 0 && (
            <p className="modal-side-panel__displayed-help">Click the radio to choose which pillar is displayed on the card.</p>
          )}

          {/* Displayed elements section for multiple selection */}
          {!isSingleSelection && displayedElements.length > 0 && (
            <div className="modal-side-panel__displayed">
              <div className="modal-side-panel__displayed-header">
                <h4>Display Order ({displayedElements.length}):</h4>
                <div className="sort-container">
                                      <button
                      className="sort-btn"
                      onClick={() => {
                        console.log('🔄 Sort button clicked, current showSortMenu:', showSortMenu);
                        setShowSortMenu(!showSortMenu);
                      }}
                      title="Sort displayed elements"
                    >
                      🔄 Sort
                    </button>
                  {showSortMenu && displayedElements.length > 1 && (
                    <div className="sort-menu">
                                              <button 
                          className={`sort-option ${sortType === 'name-asc' ? 'active' : ''}`}
                          onClick={() => {
                            console.log('🔄 Sort option clicked: name-asc');
                            setSortType('name-asc');
                            handleSortWithType('name-asc');
                            setShowSortMenu(false);
                          }}
                        >
                          A → Z
                        </button>
                        <button 
                          className={`sort-option ${sortType === 'name-desc' ? 'active' : ''}`}
                          onClick={() => {
                            console.log('🔄 Sort option clicked: name-desc');
                            setSortType('name-desc');
                            handleSortWithType('name-desc');
                            setShowSortMenu(false);
                          }}
                        >
                          Z → A
                        </button>
                        <button 
                          className={`sort-option ${sortType === 'newest' ? 'active' : ''}`}
                          onClick={() => {
                            console.log('🔄 Sort option clicked: newest');
                            setSortType('newest');
                            handleSortWithType('newest');
                            setShowSortMenu(false);
                          }}
                        >
                          Newest
                        </button>
                        <button 
                          className={`sort-option ${sortType === 'oldest' ? 'active' : ''}`}
                          onClick={() => {
                            console.log('🔄 Sort option clicked: oldest');
                            setSortType('oldest');
                            handleSortWithType('oldest');
                            setShowSortMenu(false);
                          }}
                        >
                          Oldest
                        </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-side-panel__displayed-chips">
                {displayedElements.map((itemId, index) => {
                  const item = elements.find(e => e._id === itemId);
                  return item ? (
                    <span 
                      key={itemId} 
                      className={`displayed-chip ${draggedElement === itemId ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, itemId)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, itemId)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleDisplayedToggle(itemId)}
                      title={`${index + 1}. Drag to reorder or click to remove`}
                    >
                      <span className="displayed-chip-number">{index + 1}</span>
                      {item.name}
                    </span>
                  ) : null;
                })}
              </div>
              <p className="modal-side-panel__displayed-help">
                Drag chips to reorder. First element shows on card.
              </p>
            </div>
          )}



          {/* Quick Add Section - Shows different content based on field type */}
          {field === 'pillar' ? (
            // Quick Add Pillar
            <div className="modal-side-panel__quick-add">
              <h4>Quick Add New Pillar</h4>
              {!showQuickAddPillar ? (
                <button
                  className="btn btn--green btn--small"
                  onClick={() => setShowQuickAddPillar(true)}
                  style={{ marginBottom: '1rem' }}
                >
                  Add New Pillar
                </button>
              ) : (
                <form onSubmit={handleQuickAddPillar} className="modal-side-panel__quick-add-form">
                  <div className="quick-add-form-group">
                    <input
                      type="text"
                      name="name"
                      placeholder="Pillar name..."
                      value={quickAddForm.name}
                      onChange={handleQuickAddInputChange}
                      className="modal-side-panel__quick-add-input"
                      required
                    />
                  </div>
                  <div className="quick-add-form-group">
                    <select
                      name="pillarParent"
                      value={quickAddForm.pillarParent || ''}
                      onChange={handleQuickAddInputChange}
                      className="modal-side-panel__quick-add-select"
                    >
                      <option key="" value="">No Parent (Top Level)</option>
                      {allPillars.map(pillar => {
                        let displayText = pillar.name;
                        if (pillar.pillarParent && pillar.L1pillar) {
                          if (
                            typeof pillar.pillarParent === 'object' &&
                            pillar.pillarParent.name === pillar.L1pillar
                          ) {
                            displayText += ` (${pillar.pillarParent.name})`;
                          } else if (typeof pillar.pillarParent === 'object') {
                            displayText += ` (P: ${pillar.pillarParent.name}, L1: ${pillar.L1pillar})`;
                          }
                        } else if (pillar.pillarParent && typeof pillar.pillarParent === 'object') {
                          displayText += ` (P: ${pillar.pillarParent.name})`;
                        } else if (pillar.L1pillar) {
                          displayText += ` (L1: ${pillar.L1pillar})`;
                        }
                        return (
                          <option key={pillar._id} value={pillar._id}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {quickAddError && (
                    <div className="error-message">
                      {quickAddError}
                    </div>
                  )}
                  <div className="quick-add-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button
                      type="submit"
                      className="btn btn--green btn--small"
                      disabled={quickAddLoading || !quickAddForm.name.trim()}
                    >
                      {quickAddLoading ? 'Creating...' : 'Create Pillar'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--gray btn--small"
                      onClick={() => {
                        setShowQuickAddPillar(false);
                        setQuickAddForm({
                          name: '',
                          pillarParent: ''
                        });
                        setQuickAddError('');
                      }}
                      disabled={quickAddLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            // Quick Add Element (existing functionality)
            <div className="modal-side-panel__quick-add">
              <h4>Quick Add New Element</h4>
              <div className="modal-side-panel__quick-add-form">
                <input
                  type="text"
                  placeholder="Enter element name..."
                  value={newElementName}
                  onChange={(e) => setNewElementName(e.target.value)}
                  className="modal-side-panel__quick-add-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newElementName.trim()) {
                      handleAddNewElement();
                    }
                  }}
                />
                <button
                  className="btn btn--green btn--small"
                  onClick={handleAddNewElement}
                  disabled={!newElementName.trim() || addingElement}
                >
                  {addingElement ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          )}

          <div className="modal-side-panel__actions">
            {isSaving && (
              <div className="save-progress-container">
                <div className="save-progress-bar">
                  <div 
                    className="save-progress-fill" 
                    style={{ width: `${saveProgress}%` }}
                  ></div>
                </div>
                <div className="save-progress-text">
                  Saving... {saveProgress}%
                </div>
              </div>
            )}
            <button
              className="btn btn--gray"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="btn btn--green"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidePanel; 